import { Text, useColorMode } from '@chakra-ui/react';
import { useBIMStore } from '../state/useBIMStore';
import { useAppStore } from '../state/store';
import { useTranslation } from 'react-i18next';
/**
 * Active-tool prompt for the BIM workbench ("Click to enter wall start
 * point" style), extracted from the removed mode-level status bar so the
 * shared bottom status bar can host it.
 */
export function BimToolPrompt() {
    const { t } = useTranslation();
    // Theme-aware accent for the live status line: light blue reads well on
    // the dark toolbar, deep blue on the light one.
    const { colorMode } = useColorMode();
    const statusColor = colorMode === 'dark' ? 'blue.300' : 'blue.600';
    // Selector-based subscriptions (whole-store destructuring re-renders
    // this prompt on every project mutation).
    const appTool = useAppStore((s) => s.activeTool);
    const bimTool = useBIMStore((s) => s.activeTool);
    const activeTool = (appTool && appTool !== 'select' ? String(appTool).toUpperCase() : bimTool);
    // BIM-style hover/Tab-cycle label ("Basic Wall : Generic - 200mm")
    // written by the Select tool's cursor tracking.
    const statusText = useAppStore((s) => s.statusText);
    const selectionCount = useBIMStore((s) => s.selection.length);
    const activeSheetName = useBIMStore((s) =>
        s.project.activeSheetId ? s.project.sheets.get(s.project.activeSheetId)?.name : null
    );

    const getToolPrompt = () => {
        switch (activeTool) {
            case 'WALL':
                return t('ui.statusbar.wallPrompt');
            case 'DOOR':
                return t('ui.statusbar.doorPrompt');
            case 'WINDOW':
                return t('ui.statusbar.windowPrompt');
            case 'MOVE':
                return t('ui.statusbar.movePrompt');
            case 'ROTATE':
                return t('ui.statusbar.rotatePrompt');
            case 'MEASURE':
            case 'MANAGE_MEASURE':
                return t('ui.statusbar.measurePrompt');
            case 'SELECT':
            default:
                return t('ui.statusbar.selectPrompt');
        }
    };

    return (
        <>
            <Text fontSize="xs" color="text.secondary" minW={0} isTruncated>{getToolPrompt()}</Text>
            {statusText && (
                <Text fontSize="xs" color={statusColor} whiteSpace="nowrap" isTruncated>
                    {statusText}
                </Text>
            )}
            {activeSheetName && (
                <Text fontSize="xs" color="text.secondary" whiteSpace="nowrap">
                    {t('ui.statusbar.sheet', { name: activeSheetName })}
                </Text>
            )}
            {selectionCount > 0 && (
                <Text fontSize="xs" color="text.secondary" whiteSpace="nowrap">
                    {t('common.selectedCount', { count: selectionCount })}
                </Text>
            )}
        </>
    );
}
